import { createFileRoute, useNavigate } from "@tanstack/react-router";
import * as React from "react";
import { useMutation } from "@tanstack/react-query";
import { AiProcessingNotice } from "~/components/AiProcessingNotice";
import { requireAuth } from "~/lib/auth-guard";
import { useResumeStore } from "~/stores/resume-store";
import { orpc } from "~/lib/orpc-client";
import { useToast } from "@alchema/ui";
import * as m from "~/paraglide/messages";
import { ResumeUploadError, runResumeUpload } from "~/lib/resume/upload-import";

export const Route = createFileRoute("/resumes/new")({
  beforeLoad: async () => { await requireAuth(); },
  component: NewResumePage,
});

type Stage = "choose" | "loading";

function NewResumePage() {
  const navigate = useNavigate();
  const store = useResumeStore();
  const { toast } = useToast();
  const [stage, setStage] = React.useState<Stage>("choose");
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const createMutation = useMutation(orpc.resume.create.mutationOptions());
  const parseMutation = useMutation(orpc.resume.parseUpload.mutationOptions());

  async function startBlank() {
    setStage("loading");
    try {
      const data = await createMutation.mutateAsync({ title: "Untitled Resume" });
      store.syncFromServer(data);
      navigate({ to: "/resumes/$resumeId", params: { resumeId: data.id } });
    } catch (err) {
      console.error("[resume-create] startBlank failed:", err);
      const msg = err instanceof Error ? err.message : "Failed to create resume";
      toast(msg, "error");
      setStage("choose");
    }
  }

  function triggerUpload() {
    fileInputRef.current?.click();
  }

  async function onFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    setStage("loading");
    try {
      const data = await runResumeUpload(file, {
        parse: (input) => parseMutation.mutateAsync(input),
        persist: (input) => createMutation.mutateAsync(input),
      });
      store.syncFromServer(data);
      navigate({ to: "/resumes/$resumeId", params: { resumeId: data.id } });
    } catch (err) {
      console.error("[resume-upload] Import failed:", err instanceof Error ? err.message : "unknown error");
      let msg = err instanceof Error ? err.message : m.resumes_parse_error();
      if (err instanceof ResumeUploadError) {
        if (err.code === "type") msg = m.resumes_file_type_error();
        else if (err.code === "size") msg = m.resumes_file_size_error();
      }
      toast(msg, "error");
      setStage("choose");
    }
  }

  if (stage === "loading") {
    const message = parseMutation.isPending ? m.resumes_parsing_ai() : m.resumes_creating();
    return (
      <div className="flex h-[calc(100vh-3.5rem)] items-center justify-center animate-fade-up">
        <div className="card p-10 text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-2 border-[var(--border)] border-t-[var(--accent)]" />
          <p className="text-sm font-medium">{message}</p>
          <p className="mt-1 text-xs text-muted">{m.resumes_this_may_take_moment()}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-3.5rem)] items-center justify-center animate-fade-up">
      <div className="w-full max-w-2xl px-6">
        <div className="text-center mb-10">
          <h1 className="page-title">{m.resumes_create_title()}</h1>
          <p className="mt-2 text-sm text-muted">
            {m.resumes_create_subtitle()}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <button
            onClick={startBlank}
            className="card card-interactive card-mint group flex flex-col items-center justify-center p-5 sm:p-8 text-center animate-fade-up stagger-1"
          >
            <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-[13px] bg-[var(--bg-secondary)] transition-transform duration-300 group-hover:scale-110">
              <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" className="text-muted">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
            </div>
            <h3 className="text-base font-semibold">{m.resumes_start_blank()}</h3>
            <p className="mt-1.5 text-sm text-muted">
              {m.resumes_start_blank_desc()}
            </p>
          </button>

          <div className="space-y-3 animate-fade-up stagger-2">
            <AiProcessingNotice id="resume-upload-ai-processing-notice" kind="cv-intake" />
            <button
              onClick={triggerUpload}
              aria-describedby="resume-upload-ai-processing-notice"
              className="card card-interactive card-lavender group flex w-full flex-col items-center justify-center p-5 text-center sm:p-8"
            >
              <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-[13px] bg-[var(--bg-secondary)] transition-transform duration-300 group-hover:scale-110">
                <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" className="text-muted">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
                </svg>
              </div>
              <h3 className="text-base font-semibold">{m.resumes_upload_resume()}</h3>
              <p className="mt-1.5 text-sm text-muted">
                {m.resumes_import_ai_desc()}
              </p>
            </button>
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-muted">
          {m.resumes_import_format_hint()}
        </p>

        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          className="sr-only"
          data-testid="resume-upload-input"
          onChange={onFileSelected}
        />
      </div>
    </div>
  );
}
